import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { RESPONSE } from '../store/actionTypes';
import {
  Pagination,
  Report,
  Response,
  SubmitResponseInput,
  UpdateResponseInput,
} from './models/response';

export const response = createApi({
  reducerPath: RESPONSE,
  baseQuery,
  tagTypes: ['Response'],
  endpoints: builder => ({
    submitResponse: builder.mutation<Response, SubmitResponseInput>({
      query: variables => ({
        document: gql`
          mutation SubmitResponse($responseInput: ResponseInput!) {
            submitResponse(responseInput: $responseInput) {
              id
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.submitResponse,
    }),
    updateResponse: builder.mutation<Response, UpdateResponseInput>({
      query: variables => ({
        document: gql`
          mutation UpdateResponse($responseId: ObjectID!, $responseInput: ResponseInput!) {
            updateResponse(responseId: $responseId, responseInput: $responseInput) {
              id
            }
          }
        `,
        variables,
      }),
      invalidatesTags: ['Response'],
      transformResponse: (response: any) => response?.updateResponse,
    }),
    saveResponseToCache: builder.mutation<
      Report[][],
      { userId: string; tag: string; responseCacheInput: Report[][] }
    >({
      query: variables => ({
        document: gql`
          mutation SaveResponseToCache(
            $userId: String
            $tag: String
            $responseCacheInput: [[ReportInput]]!
          ) {
            saveResponseToCache(
              userId: $userId
              tag: $tag
              responseCacheInput: $responseCacheInput
            ) {
              value
              label
              position
              question
            }
          }
        `,
        variables,
      }),
      invalidatesTags: ['Response'],
      transformResponse: (response: any) => response?.saveResponseToCache,
    }),
    getResponseFromCache: builder.query<[[Report]], { userId: string; tag: string }>({
      query: variables => ({
        document: gql`
          query GetResponseFromCache($userId: String, $tag: String) {
            getResponseFromCache(userId: $userId, tag: $tag) {
              value
              label
              position
              question
            }
          }
        `,
        variables,
      }),
      providesTags: ['Response'],
      transformResponse: (response: any) => response?.getResponseFromCache,
    }),
    resetResponseCache: builder.mutation<[[Report]], { userId: string; tag: string }>({
      query: variables => ({
        document: gql`
          mutation ResetResponseCache($userId: String, $tag: String) {
            resetResponseCache(userId: $userId, tag: $tag) {
              value
              label
              position
              question
            }
          }
        `,
        variables,
      }),
      invalidatesTags: ['Response'],
      transformResponse: (response: any) => response?.resetResponseCache,
    }),
    getResponsesByFormName: builder.query<
      { data: Response[]; pagination: Pagination },
      { name: string; page: number; limit: number; offset: number }
    >({
      query: variables => ({
        document: gql`
          query GetResponsesByFormName($name: String!, $page: Int, $limit: Int, $offset: Int) {
            getResponsesByFormName(name: $name, page: $page, limit: $limit, offset: $offset) {
              data {
                id
                meta {
                  created
                }
                data
                status
                report {
                  value
                  label
                  position
                  question
                }
                reporter
                user {
                  email
                  preferences {
                    notifications {
                      medium
                      status
                    }
                  }
                }
              }
              pagination {
                items
                pages
              }
            }
          }
        `,
        variables,
      }),
      providesTags: ['Response'],
      transformResponse: (response: any) => response?.getResponsesByFormName,
    }),
    getResponsesByID: builder.query<Response, { responseId: string; formName: string }>({
      query: variables => ({
        document: gql`
          query GetResponseById($responseId: ObjectID!, $formName: FreeFormName!) {
            getResponseById(id: $responseId, formName: $formName) {
              id
              meta {
                created
              }
              data
              formName
              status
              report {
                value
                label
                position
                question
              }
              reporter
              user {
                email
                preferences {
                  notifications {
                    medium
                    status
                  }
                }
              }
            }
          }
        `,
        variables,
      }),
      providesTags: ['Response'],
      transformResponse: (response: any) => response?.getResponseById,
    }),
    getMyResponses: builder.query<Response[], { formName: string; page: number; limit: number }>({
      query: variables => ({
        document: gql`
          query GetMyResponses($formName: String!, $page: Int, $limit: Int) {
            getMyResponses(formName: $formName, page: $page, limit: $limit) {
              id
              meta {
                created
              }
              data
              status
              report {
                value
                label
                position
                question
              }
              reporter
              user {
                email
                preferences {
                  notifications {
                    medium
                    status
                  }
                }
              }
            }
          }
        `,
        variables,
      }),
      providesTags: ['Response'],
      transformResponse: (response: any) => response?.getMyResponses,
    }),
  }),
});

export const {
  useSubmitResponseMutation, // not used in the UI, but used in orchestration
  useUpdateResponseMutation,
  useSaveResponseToCacheMutation, // not used in the UI, but used in orchestration
  useGetResponseFromCacheQuery, // not used in the UI, but used in orchestration
  useLazyGetResponseFromCacheQuery, // not used in the UI, but used in orchestration
  useResetResponseCacheMutation, // not used in the UI, but used in orchestration
  useGetResponsesByFormNameQuery,
  useLazyGetResponsesByFormNameQuery, // not used in the UI, but used in orchestration
  useGetResponsesByIDQuery,
  useLazyGetMyResponsesQuery,
} = response;
